import { useMemo } from 'react'
import styled from 'styled-components'
import { DownOutlined } from '@ant-design/icons'
import { Button, Divider, Dropdown, Space, Tooltip, Badge, Select, message } from 'antd'
import { IconFont, SvgIcon } from '@/components'
import { formatDateTime } from '@/utils/date'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import { CSDataType } from '../task-list-main'
import { t } from '@/i18n'

interface TableColumnsProps {
  groupList: any[]
  groupSelectOpen: boolean
  setGroupSelectOpen: (open: boolean) => void
  buildItems: (id: string, isRemove: boolean) => JSX.Element
  buildSubItems: (
    taskName: string,
    id: string,
    taskId: string,
    status: string,
    isReport: boolean,
    isRemove: boolean,
    isRetest: boolean,
  ) => JSX.Element
  handleOpenTask: (id: string, taskStatus: number) => void
  viewData: (id: string) => void
  handleStopTask: (id: string, taskId: string) => void
  setSearchParams: (params: Record<string, any>) => void
  searchParams: Record<string, any>
}

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`

export const useTableColumns = ({
  groupList,
  groupSelectOpen,
  setGroupSelectOpen,
  buildItems,
  buildSubItems,
  handleOpenTask,
  viewData,
  handleStopTask,
  searchParams,
  setSearchParams,
}: TableColumnsProps) => {
  const columns = [
    {
      title: t('任务名称'),
      dataIndex: 'name',
      key: 'name',
      width: '11%',
      ellipsis: true,
      render: (_, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={record.name} getPopupContainer={() => document.body}>
            <span
              style={{
                maxWidth: 'calc(100% - 24px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </span>
          </Tooltip>
          {record.devices?.length > 0 && (
            <Tooltip
              getPopupContainer={() => document.body}
              overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
              title={
                <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                  <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                  {record.devices.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 5,
                        flexWrap: 'nowrap',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '1 1 auto',
                          minWidth: 0,
                        }}
                      >
                        {item.deviceType} | {item.deviceName} | {item.deviceIp}
                      </span>
                      <Badge
                        style={{ margin: '0 8px', flexShrink: 0 }}
                        status={item.isOnline ? 'success' : 'default'}
                      />
                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {item.isOnline ? t('在线') : t('离线')}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              <SvgIcon
                name={record.devices.some(d => d.isOnline) ? '设备' : '设备灰'}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('任务综合评分'),
      dataIndex: 'score',
      key: 'score',
      width: '13%',
      render: ({
        score,
        riskLevel,
        className,
      }: {
        score: number
        riskLevel: string
        className: string
      }) => (
        <span>
          <span className="score">{score}</span>
          <span className="divider">|</span>
          <span className={className}>{riskLevel}</span>
        </span>
      ),
      sorter: (a: CSDataType, b: CSDataType) => (a?.score || 0) - (b?.score || 0),
    },
    {
      title: t('运行状态分布'),
      dataIndex: 'statusList',
      key: 'statusList',
      width: '18%',
      render: statusList => (
        <div style={{ display: 'flex' }}>
          {statusList?.map(status => (
            <div key={status.name} className={`status ${status.className}`}>
              <span>{status.name}</span>
              <span className="count">{status.count}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('目标主体'),
      dataIndex: 'email',
      key: 'email',
      width: '18%',
      ellipsis: true,
      render: (_, record) => (
        <Tooltip
          title={
            record?.verificationMode?.agentToContainer?.hostName
              ? `${record?.verificationMode?.agentToContainer?.hostName}|${record?.verificationMode?.agentToContainer?.hostIp}`
              : record?.verificationMode?.containerToListener?.hostName
              ? `${record?.verificationMode?.containerToListener?.hostName}|${record?.verificationMode?.containerToListener?.hostIp}`
              : '-'
          }
          getPopupContainer={() => document.body}
        >
          <span>
            {record?.verificationMode?.agentToContainer?.hostName
              ? `${record?.verificationMode?.agentToContainer?.hostName}|${record?.verificationMode?.agentToContainer?.hostIp}`
              : record?.verificationMode?.containerToListener?.hostName
              ? `${record?.verificationMode?.containerToListener?.hostName}|${record?.verificationMode?.containerToListener?.hostIp}`
              : '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('执行类型'),
      dataIndex: 'kind',
      key: 'kind',
      width: '10%',
      render: (kind: string, { executeTimesUsed, executeTimesLimit }) => (
        <div
          className={`kind-wrapper${['立即', '预定'].includes(kind) ? ' kind-immediately' : ''}`}
        >
          <span className="kind">{kind}</span>
          {!['立即', '预定'].includes(kind) && (
            <>
              <span>{executeTimesUsed || 0}</span>
              <span className="divider">|</span>
              <span>{executeTimesLimit === 0 ? '∞' : executeTimesLimit}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('优先级'),
      dataIndex: 'priority',
      key: 'priority',
      width: '7%',
      ellipsis: true,
      sorter: (a: CSDataType, b: CSDataType) => (a?.priority || 0) - (b?.priority || 0),
    },
    {
      title: t('分组'),
      dataIndex: 'groupId',
      key: 'groupId',
      width: '10%',
      filteredValue: searchParams?.groupId ? searchParams?.groupId : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择攻击类型')}
            value={props.selectedKeys as string[]}
            open={groupSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setGroupSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
              setSearchParams(prev => ({ ...prev, groupId: value }))
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                      setSearchParams(prev => ({ ...prev, groupId: undefined }))
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setGroupSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={groupList.map(type => ({
              label: type.name,
              value: String(type.id),
            }))}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),
      render: (_, record) => <span>{record.groupName || '-'}</span>,
    },
    {
      title: t('首次运行时间'),
      dataIndex: 'firstExecuteTime',
      key: 'firstExecuteTime',
      width: '10%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('最近结束时间'),
      dataIndex: 'latestExecuteTime',
      key: 'latestExecuteTime',
      width: '10%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      fixed: 'right',
      width: '25%',
      render: (_, { id, taskStatus, exceptReason, isRemove }) => (
        <Space size="middle">
          {taskStatus && (
            <div className="stop-btn">
              {['等待中', '中断', '暂停', '错误'].includes(taskStatus) && (
                <span
                  onClick={() => {
                    if (taskStatus === '中断' || taskStatus === '错误') {
                      message.warn({
                        className: 'task-warn',
                        content: (
                          <div>
                            <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                            {t('异常原因：')}
                            {exceptReason}
                          </div>
                        ),
                      })
                      return
                    }
                    handleOpenTask(id, 2)
                  }}
                >
                  <span className="text">{t('开启')}</span>
                </span>
              )}
              {['进行中'].includes(taskStatus) && (
                <span onClick={() => handleOpenTask(id, 1)}>
                  <span className="text">{t('暂停')}</span>
                </span>
              )}
            </div>
          )}
          <div className="view-btn" onClick={() => viewData(id)}>
            <span className="text">{t('查看')}</span>
          </div>
          <Dropdown
            overlay={buildItems(id, isRemove)}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 9999 }}
          >
            <span>
              {t('更多')} <DownOutlined />
            </span>
          </Dropdown>
        </Space>
      ),
    },
  ]

  const subColumns = useMemo(() => {
    const arr = [
      {
        title: t('子任务名称'),
        dataIndex: 'taskName',
        key: 'taskName',
        width: '20%',
        ellipsis: true,
        render: (_, record) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title={record.taskName} getPopupContainer={() => document.body}>
              <span
                style={{
                  maxWidth: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.taskName}
              </span>
            </Tooltip>
            {record?.devices?.length > 0 && (
              <Tooltip
                getPopupContainer={() => document.body}
                overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                title={
                  <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                    <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                    {record?.devices?.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 5,
                          flexWrap: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {item.deviceType} | {item.deviceName} | {item.deviceIp}
                        </span>
                        <Badge
                          style={{ margin: '0 8px', flexShrink: 0 }}
                          status={item.isOnline ? 'success' : 'default'}
                        />
                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {item.isOnline ? t('在线') : t('离线')}
                        </span>
                      </div>
                    ))}
                  </div>
                }
              >
                <SvgIcon
                  name={record?.devices?.some(d => d.isOnline) ? '设备' : '设备灰'}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: t('任务评分'),
        dataIndex: 'score',
        key: 'score',
        width: '16%',
        render: ({
          score,
          riskLevel,
          className,
        }: {
          score: number
          riskLevel: string
          className: string
        }) => (
          <span>
            <span className="score">{typeof score === 'number' ? score / 100 : score}</span>
            <span className="divider">|</span>
            <span className={className}>{riskLevel}</span>
          </span>
        ),
      },
      {
        title: t('运行状态'),
        dataIndex: 'status',
        key: 'status',
        width: '16%',
        render: (status, { resourceSendTotal, resourceTotal, exceptReason }) => (
          <div className="status-wrapper">
            <div>
              <span>{resourceSendTotal || 0}</span>
              <span className="divider">|</span>
              <span>{resourceTotal}</span>
            </div>
            <span className={`status ${status?.className}`}>{status?.label}</span>
            {status?.label === '中断' && (
              <Tooltip title={exceptReason} getPopupContainer={() => document.body}>
                <IconFont
                  type={'icon-jinggao_jinggao'}
                  style={{ color: '#F53F3F', fontSize: '14px' }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: t('创建时间'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: '10%',
        ellipsis: true,
        render: (text: string) => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('开始时间'),
        dataIndex: 'startTime',
        key: 'startTime',
        width: '10%',
        ellipsis: true,
        render: (startTime: string) => (
          <Tooltip
            title={startTime === '-' ? '-' : formatDateTime(startTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {startTime === '-' ? '-' : formatDateTime(startTime, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('结束时间'),
        dataIndex: 'endTime',
        key: 'endTime',
        width: '10%',
        ellipsis: true,
        render: (endTime: string) => (
          <Tooltip
            title={endTime === '-' ? '-' : formatDateTime(endTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {endTime === '-' ? '-' : formatDateTime(endTime, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('操作'),
        key: 'action',
        fixed: 'right',
        width: '18%',
        render: (
          _,
          { taskName, taskId, id, status, isStop, isView, isReport, isRetest, isRemove },
        ) => (
          <Space size="middle">
            {isStop && (
              <div onClick={() => handleStopTask(id, taskId)} className="stop-btn">
                <IconFont type="icon-zanting2" />
                <span style={{ marginLeft: 6 }}>{t('停止')}</span>
              </div>
            )}
            <div
              className={isView ? 'view-btn' : 'view-btn-disabled'}
              onClick={() =>
                isView &&
                window.open(
                  `/validation/cloud/containersecurity?tab=task&name=detail&id=${id}&task_id=${taskId}`,
                  '_blank',
                )
              }
            >
              <span className="text">{t('查看')}</span>
            </div>
            <Dropdown
              overlay={buildSubItems(
                taskName,
                id,
                taskId,
                status?.label,
                isReport,
                isRemove,
                isRetest,
              )}
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 9999 }}
            >
              <span>
                {t('更多')} <DownOutlined />
              </span>
            </Dropdown>
          </Space>
        ),
      },
    ]

    return arr
  }, [])

  return { columns: columns as any, subColumns: subColumns as any }
}
